import React from "react";
import { useLang } from "../../contexts/LanguageContext";
import Reveal from "../Reveal";
import { IMAGES } from "../../lib/i18n";

export default function Lanzadera() {
  const { t } = useLang();
  const c = t.lanzadera;
  return (
    <section id="lanzadera" data-testid="lanzadera-section" className="bg-olive text-ivory py-24 sm:py-32 relative overflow-hidden">
      <div
        aria-hidden
        className="hidden lg:block absolute -left-10 bottom-10 vertical-rl font-serif italic text-[10rem] leading-none text-ivory/[0.05] select-none"
      >
        lanzadera
      </div>
      <div className="mx-auto max-w-[1500px] px-5 sm:px-8 relative">
        <div className="grid grid-cols-12 gap-y-10 gap-x-6 mb-16">
          <Reveal className="col-span-12 lg:col-span-3 flex items-start gap-4">
            <span className="block h-px w-12 bg-terracotta mt-3" aria-hidden />
            <span className="eyebrow">{c.eyebrow}</span>
          </Reveal>
          <Reveal as="h2" className="col-span-12 lg:col-span-9 h-section-lg">
            {c.title}
          </Reveal>
          <Reveal className="col-span-12 lg:col-span-8 lg:col-start-4">
            <p className="text-lg text-ivory/80 leading-relaxed">{c.body}</p>
          </Reveal>
        </div>

        {/* Editorial drone image */}
        <Reveal className="mb-20">
          <div className="relative overflow-hidden border border-ivory/15">
            <img
              src={IMAGES.drone}
              alt={c.captions.imageAlt}
              className="w-full aspect-[21/9] object-cover"
              loading="lazy"
            />
            <div className="absolute bottom-0 left-0 right-0 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 p-5 sm:p-8 bg-gradient-to-t from-olive-deep/90 via-olive-deep/40 to-transparent">
              <div>
                <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-terracotta mb-2">
                  {c.captions.kicker}
                </div>
                <div className="font-serif italic text-2xl sm:text-3xl text-ivory leading-snug max-w-xl">
                  {c.captions.headline}
                </div>
              </div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ivory/70 whitespace-nowrap">
                {c.captions.footerTags}
              </div>
            </div>
          </div>
        </Reveal>

        {c.milestones && (
          <div className="border-t border-ivory/15 pt-12 mb-16">
            <Reveal className="grid grid-cols-12 gap-x-6 gap-y-4 mb-10">
              <div className="col-span-12 lg:col-span-4">
                <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-terracotta mb-3">{c.next90Title}</div>
              </div>
              <p className="col-span-12 lg:col-span-8 text-ivory/80 font-serif italic text-2xl leading-snug">
                {c.next90Intro}
              </p>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ivory/15">
              {c.milestones.map((m, i) => (
                <Reveal key={i} delay={i * 70} data-testid={`milestone-${i}`} className="bg-olive p-7 flex flex-col gap-3">
                  <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-terracotta">{m.tag}</div>
                  <div className="font-display text-2xl leading-tight">{m.title}</div>
                  <p className="text-ivory/70 text-sm leading-relaxed">{m.body}</p>
                </Reveal>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ivory/15">
          {c.stats.map((s, i) => (
            <Reveal key={i} delay={i * 70} data-testid={`lanzadera-stat-${i}`} className="bg-olive p-8 flex flex-col gap-3">
              <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-terracotta">{s.tag}</div>
              <div className="font-display text-3xl leading-tight">{s.title}</div>
              <p className="text-ivory/70 text-sm leading-relaxed">{s.body}</p>
            </Reveal>
          ))}
        </div>

        {c.manifesto && (
          <Reveal className="mt-20 border-t border-ivory/15 pt-12 max-w-4xl">
            <span aria-hidden className="block font-serif italic text-7xl text-terracotta/40 leading-none mb-2">&ldquo;</span>
            <p data-testid="lanzadera-manifesto" className="h-section italic leading-tight text-ivory">
              {c.manifesto}
            </p>
            <div className="mt-6 font-mono text-[10px] tracking-[0.35em] uppercase text-terracotta">
              {c.captions.manifestoSign}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
